/**
 * Transforms coverage analysis data by replacing IDs with names.
 *
 * @param data - The coverage analysis data.
 * @param redlineDatasetIdToName - Mapping of redline dataset IDs to names.
 * @param coverageAnalysisStudyIdToNameMap - Mapping of coverage analysis study IDs to names.
 * @returns Transformed data with IDs replaced by names.
 */
export const handleCoverageAnalysis = (data: any, redlineDatasetIdToName: any, coverageAnalysisStudyIdToNameMap: any) => {
  const result: any = {};

  for (const studyId in data) {
    const studyName = `${coverageAnalysisStudyIdToNameMap[studyId] || studyId}`;
    result[studyName] = {};

    for (const subType in data[studyId]) {
      result[studyName][subType] = {};

      for (const id in data[studyId][subType]) {
        const name = `${redlineDatasetIdToName[id] || id}`;
        result[studyName][subType][name] = data[studyId][subType][id];
      }
    }
  }

  return result;
};

/**
 * Transforms ADAS reports data by replacing IDs with names.
 *
 * @param data - The ADAS reports data.
 * @param adasProgramReportIdToName - Mapping of ADAS program report IDs to names.
 * @param adasProgramIdToNameMap - Mapping of ADAS program IDs to names.
 * @returns Transformed data with IDs replaced by names.
 */
export const handleAdasReports = (data: any, adasProgramReportIdToName: any, adasProgramIdToNameMap: any) => {
  const result: any = {};

  for (const adasProgramId in data) {
    const adasProgramName = `${adasProgramIdToNameMap[adasProgramId] || adasProgramId}`;
    result[adasProgramName] = {};

    for (const subType in data[adasProgramId]) {
      result[adasProgramName][subType] = {};

      for (const id in data[adasProgramId][subType]) {
        const name = `${adasProgramReportIdToName[id] || id}`;
        result[adasProgramName][subType][name] = data[adasProgramId][subType][id];
      }
    }
  }

  return result;
};

/**
 * Transforms green lines data by replacing IDs with names.
 *
 * @param data - The green lines data.
 * @param greenlineStudyIdToNameMap - Mapping of greenline study IDs to names.
 * @param greenlineIdToName - Mapping of greenline IDs to names.
 * @returns Transformed data with IDs replaced by names.
 */
export const handleGreenLines = (data: any, greenlineStudyIdToNameMap: any, greenlineIdToName: any) => {
  const result: any = {};

  for (const studyId in data) {
    const studyName = `${greenlineStudyIdToNameMap[studyId] || studyId}`;
    result[studyName] = {};

    for (const id in data[studyId]) {
      const name = `${greenlineIdToName[id] || id}`;
      result[studyName][name] = data[studyId][id];
    }
  }

  return result;
};

/**
 * Replaces IDs with names in the given data for various application types.
 *
 * @param data - The data containing IDs.
 * @param variantIdToName - Mapping of variant IDs to names.
 * @param redlineDatasetIdToName - Mapping of redline dataset IDs to names.
 * @param adasProgramReportIdToName - Mapping of ADAS program report IDs to names.
 * @param adasProgramIdToNameMap - Mapping of ADAS program IDs to names.
 * @param coverageAnalysisStudyIdToNameMap - Mapping of coverage analysis study IDs to names.
 * @param greenlineStudyIdToNameMap - Mapping of greenline study IDs to names.
 * @param greenlineIdToName - Mapping of greenline IDs to names.
 * @returns Transformed data with IDs replaced by names.
 */
export const replaceIdsWithNames = (data: any, variantIdToName: any, redlineDatasetIdToName: any, adasProgramReportIdToName: any, adasProgramIdToNameMap: any,
                                    coverageAnalysisStudyIdToNameMap: any, greenlineStudyIdToNameMap: any, greenlineIdToName: any) => {
  const result: any = {};

  for (const variantId in data) {
    const variantName = `${variantIdToName[variantId] || variantId}`;
    result[variantName] = {};

    for (const appType in data[variantId]) {
      if (appType === 'Coverage Analysis') {
        result[variantName][appType] = handleCoverageAnalysis(data[variantId][appType], redlineDatasetIdToName, coverageAnalysisStudyIdToNameMap);
      } else if (appType === 'ADAS Reports') {
        result[variantName][appType] = handleAdasReports(data[variantId][appType], adasProgramReportIdToName, adasProgramIdToNameMap);
      } else if (appType === 'Green Lines') {
        result[variantName][appType] = handleGreenLines(data[variantId][appType], greenlineStudyIdToNameMap, greenlineIdToName);
      } else {
        result[variantName][appType] = data[variantId][appType];
      }
    }
  }

  return result;
};